import type { Definitions } from "@/lib/element-registry";
import { joinWalkthrough } from "@/lib/figure-status";

export type WalkthroughChange =
  | { kind: "walkthrough"; fileId: string; walkthrough: string }
  | { kind: "walkthrough-accepted"; fileId: string; accepted: false }
  | {
      kind: "walkthrough-accepted";
      fileId: string;
      accepted: true;
      walkthrough: string;
    }
  | {
      kind: "adopt-transcript";
      fileId: string;
      clipId: string;
      transcript: string;
    };

/**
 * The walk-through family of folds, over whatever set is freshest.
 *
 * Any change to the words un-confirms them: acceptance is of particular words,
 * and different words have not been accepted. The adopt fold is idempotent by
 * the recorded clip ids, so a refold or a second arrival changes nothing.
 */
export function foldWalkthrough(
  change: WalkthroughChange,
  definitions: Definitions,
  rowFor: (
    definitions: Definitions,
    fileId: string,
  ) => Definitions["figures"][number],
  figureChanged: (
    definitions: Definitions,
    fileId: string,
    change: Partial<Definitions["figures"][number]>,
  ) => Definitions,
): Definitions {
  switch (change.kind) {
    case "walkthrough":
      // The same words again are no edit: they leave a confirmation of those
      // words standing, which a save queued behind the confirm would lift.
      if (rowFor(definitions, change.fileId).walkthrough === change.walkthrough)
        return definitions;
      return figureChanged(definitions, change.fileId, {
        walkthrough: change.walkthrough,
        walkthrough_accepted: false,
      });
    case "walkthrough-accepted":
      // An accept is of particular words, so it writes the words it carries
      // beside the bit, like any edit. Refolded over a set another session
      // rewrote, it re-asserts exactly what was accepted and can never pair
      // a confirmation with words the attorney has not seen.
      if (change.accepted) {
        return figureChanged(definitions, change.fileId, {
          walkthrough: change.walkthrough,
          walkthrough_accepted: true,
        });
      }
      return figureChanged(definitions, change.fileId, {
        walkthrough_accepted: false,
      });
    case "adopt-transcript": {
      const row = rowFor(definitions, change.fileId);
      const adoptedIds = row.walkthrough_adopted_clip_ids ?? [];
      if (adoptedIds.includes(change.clipId)) return definitions;
      return figureChanged(definitions, change.fileId, {
        walkthrough: joinWalkthrough(row.walkthrough, change.transcript),
        walkthrough_adopted_clip_ids: [...adoptedIds, change.clipId],
        walkthrough_accepted: false,
      });
    }
  }
}
